import React, { createContext, useContext, useState, useRef } from "react";
import axios from "axios";
import { API_BASE_URL } from "@config/config";
import { AuthContext } from "@context/AuthContext";
import { getSessionButtonText, getSessionType } from "@utils/ticketUtils";
import { buildSessionSearchParams } from "@utils/urlUtils";

export const SearchContext = createContext({});

const SearchContextProvider = (props) => {
    const { isLoggedIn } = useContext(AuthContext);
    const [searchOptions, setSearchOptions] = useState([]);
    const [isSearchOptionsLoading, setIsSearchOptionsLoading] = useState(false);
    const [venue, setVenue] = useState({});
    const [movie, setMovie] = useState({});
    const [movieTitle, setMovieTitle] = useState(null);
    const [movieImage, setMovieImage] = useState(null);
    const [venueName, setVenueName] = useState("");
    const [venueHomeSearch, setVenueHomeSearch] = useState("");
    const [experienceType, setExperienceType] = useState(null);
    const [selectedExperiences, setSelectedExperiences] = useState([]);
    const [sessionDate, setSessionDate] = useState(null);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [session, setSession] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [primarySessions, setPrimarySessions] = useState([]);
    const [secondarySessions, setSecondarySessions] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableClasses, setAvailableClasses] = useState([]);
    const [sessionsByVenue, setSessionsByVenue] = useState({});
    const [participatingCinemas, setParticipatingCinemas] = useState([]);
    const [hasFetchedSearchOptions, setHasFetchedSearchOptions] = useState(false);
    const hasFetchedSearchOptionsRef = useRef(false);
    const { children } = props;

    const fetchMovieSessions = ({
        venueName,
        movieTitle,
        sessionDate,
        lat,
        lng,
        ticket_class_id,
        search_distance = 25,
    } = {}) => {

        setIsLoadingSessions(true);
        const params = buildSessionSearchParams({
            venueName,
            movieTitle,
            sessionDate,
            lat,
            lng,
            ticket_class_id,
            search_distance,
        });

        const queryString = params.toString();
        const endpoint     = queryString
            ? `${API_BASE_URL}/session?${queryString}`
            : `${API_BASE_URL}/session`;
        axios.get(endpoint)
            .then((response) => {
                const sessionData = response.data.data.map((item) => {
                    const sessionType = getSessionType(item);
                    return {
                        ...item,
                        experience_type: sessionType,
                        venue_image: item.venue_image,
                        ticket_price_cents: parseInt(item.ticket_price_cents, 10),
                        rrp_pricing_cents: item.rrp_pricing_cents,
                    };
                });
                setSessions(sessionData);
            })
            .catch((error) => {
                console.log(error);
                setSessions([]);
                if (isLoggedIn && error?.response?.status === 401) {
                    window.location = "/";
                }
            })
            .finally(() => {
                setIsLoadingSessions(false);
            });
    };

    const fetchVenueSessions = ({ venueId } = {}) => {
        setIsLoadingSessions(true);
        setSessionsByVenue({});
        axios
            .get(`${API_BASE_URL}/get-venue-sessions?venue_id=${venueId}`)
            .then(function(response) {
                setIsLoadingSessions(false);
                setSessionsByVenue(response.data.sessions);
                setAvailableDates(response.data.availableDates);
                setAvailableClasses(response.data.availableClasses);
            })
            .catch((error) => {
                setIsLoadingSessions(false);
                setSessionsByVenue({});
            });
    };

    const fetchSearchOptions = () => {
        if (hasFetchedSearchOptionsRef.current) return;

        hasFetchedSearchOptionsRef.current = true;

        setIsSearchOptionsLoading(true);
        axios
            .get(`${API_BASE_URL}/search`)
            .then((response) => {
                const searchOptions = response.data.data.map((item) => ({
                    ...item,
                    label: `${item.name} (${item.type})`,
                    value: item.name,
                    url: item.link_url,
                }));
                setSearchOptions(searchOptions);
                const cinemas = response.data.data.filter(item => item.type === "cinema");
                setParticipatingCinemas(cinemas);
                setIsSearchOptionsLoading(false);
            })
            .catch((error) => {
                setSearchOptions([]);
                setIsSearchOptionsLoading(false);
            });
    };

    const fetchVenue = (id, venue_name) => {
        axios
            .get(`${API_BASE_URL}/venue/${id}/${venue_name}`)
            .then(function(response) {
                setVenue(response.data.data[0]);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const fetchMovie = async (id, movieName) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/movie/${id}/${movieName}`);
            if (response.status === 200) {
                const movieData = response.data.data.movie;
                setMovie(movieData);
                setMovieTitle(movieData.title);
                if (movieData?.images?.length > 0 && movieData.images[0]?.local_url) {
                    setMovieImage(movieData.images[0].local_url);
                } else {
                    setMovieImage(null);
                }
                return movieData;
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const fetchSessionsByMovie = ({
        venueName,
        movieId,
        sessionDate,
        lat,
        lng,
        ticketClassId,
        search_distance = 25,
    } = {}) => {

        setIsLoadingSessions(true);
        const params = buildSessionSearchParams({
            venueName,
            movieId,
            sessionDate,
            lat,
            lng,
            ticketClassId,
            search_distance,
        });
        
        const queryString = params.toString();
        const endpoint = queryString
            ? `${API_BASE_URL}/sessions?${queryString}`
            : `${API_BASE_URL}/sessions`;
            
        axios.get(endpoint)
            .then((response) => {
                setPrimarySessions(response.data.primarySessions);
                setSecondarySessions(response.data.secondarySessions);
                setAvailableDates(response.data.availableDates);
                setAvailableClasses(response.data.availableClasses);
            })
            .catch((error) => {
                console.log(error);
                setPrimarySessions([]);
                setSecondarySessions([]);
                setAvailableDates([]);
                setAvailableClasses([]);
                if (isLoggedIn && error?.response?.status === 401) {
                    window.location = "/";
                }
            })
            .finally(() => {
                setIsLoadingSessions(false);
            });
    };

    return (
        <SearchContext.Provider
            value={{
                venue,
                movie,
                fetchMovie,
                venueName,
                setVenueName,
                fetchVenue,
                fetchSearchOptions,
                isSearchOptionsLoading,
                isLoadingSessions,
                setIsLoadingSessions,
                searchOptions,
                movieTitle,
                setMovieTitle,
                movieImage,
                sessionDate,
                setSessionDate,
                sessions,
                setSessions,
                session,
                setSession,
                fetchVenueSessions,
                sessionsByVenue,
                participatingCinemas,
                experienceType,
                setExperienceType,
                selectedExperiences,
                setSelectedExperiences,
                setMovie,
                fetchMovieSessions,
                hasFetchedSearchOptions,
                fetchSessionsByMovie,
                primarySessions,
                secondarySessions,
                availableDates,
                availableClasses,
                venueHomeSearch,
                setVenueHomeSearch,
            }}
        >
            {children}
        </SearchContext.Provider>
    );
};

export default SearchContextProvider;
